<template>
  <div class="quotation-row-view py-2" :class="{ 'quotation-row-view--conditional': row.conditional }">
    <v-row dense align="start">
      <v-col cols="12" md="6" class="d-flex">
        <v-icon v-if="!readonly" small class="drag-handle mr-1 mt-1" :title="$t('quotations.calc.drag_row')">mdi-drag</v-icon>
        <v-icon small class="mr-2 mt-1 no-gradient" :title="$t(`quotations.calc.row_types.${rowTypeKey}`)">{{ typeIcon }}</v-icon>
        <div>
          <template v-if="row.rowType === 'WORK'">
            <div class="body-2">{{ title || $t('quotations.calc.method_missing') }}</div>
            <div v-if="row.description" class="body-2 row-description">{{ row.description }}</div>
          </template>
          <div v-else class="body-2 row-description">{{ row.description || $t('quotations.calc.no_description') }}</div>
          <div v-if="row.conditional && row.conditionText" class="caption">{{ $t('quotations.calc.condition') }}: {{ row.conditionText }}</div>
          <div class="caption grey--text">{{ projectText }}</div>
        </div>
      </v-col>
      <v-col cols="4" md="2" class="body-2 text-md-right">{{ quantityText }}</v-col>
      <v-col cols="4" md="2" class="body-2 text-md-right">{{ priceText }}</v-col>
      <v-col cols="4" md="2" class="body-2 text-right d-flex justify-end align-start">
        <span v-if="row.conditional" class="caption grey--text mr-1">{{ $t('quotations.calc.not_in_total') }}</span>
        <span v-else-if="total !== null" class="font-weight-medium mr-1">{{ $formatNumber(total, '0,0.00') }} €</span>
        <span v-else class="grey--text mr-1">–</span>
        <template v-if="!readonly">
          <v-btn icon x-small :disabled="saving" :title="$t('quotations.calc.copy_row')" @click="$emit('copy')">
            <v-icon small class="no-gradient">mdi-content-copy</v-icon>
          </v-btn>
          <v-btn icon x-small :disabled="saving" :title="$t('quotations.calc.edit_row')" @click="$emit('edit')">
            <v-icon small class="no-gradient">mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon x-small :disabled="saving" :title="$t('quotations.calc.delete_row')" @click="$emit('remove')">
            <v-icon small class="no-gradient" color="error">delete</v-icon>
          </v-btn>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { rowTotal, workDefs, workTitle, priceUnit } from '@/helpers/quotationCalc';
import { isSiteAdditionalItem } from '@/helpers/itemTypes';

// Tarjousrivi luettavana, kuten tarjousdokumentissa. Muokkaus avataan kynästä.
export default {
  name: 'QuotationRowView',
  props: {
    row: { type: Object, required: true },
    workMethods: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
  },
  computed: {
    rowTypeKey() {
      return this.row.conditional ? 'CONDITIONAL' : this.row.rowType;
    },
    typeIcon() {
      if (this.row.conditional) return 'mdi-plus-circle-outline';
      return { WORK: 'mdi-shovel', ITEM: 'mdi-package-variant' }[this.row.rowType] || 'mdi-text';
    },
    method() {
      return this.workMethods.find(m => m.id === this.row.workMethodId);
    },
    defs() {
      return workDefs(this.method);
    },
    title() {
      return workTitle(this.method, this.defs, this.row.attributeValues);
    },
    total() {
      if (this.row.quantity == null || this.row.unitPrice == null) return null;
      return rowTotal(this.row.quantity, this.row.unitPrice);
    },
    quantityText() {
      if (this.row.conditional) return '';
      return `${this.row.quantity != null ? this.$formatNumber(this.row.quantity, '0,0.[00]') : '–'} ${this.row.unit || ''}`;
    },
    priceText() {
      const unit = priceUnit(this.row.unit);
      return this.row.unitPrice == null ? `– ${unit}` : `${this.$formatNumber(this.row.unitPrice, '0,0.[00]')} ${unit}`;
    },
    parameterTexts() {
      const values = this.row.attributeValues || [];
      return this.defs
        .slice()
        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
        .map(def => {
          const v = values.find(x => x.defId === def.id);
          return v ? this.parameterText(def, v) : null;
        })
        .filter(Boolean);
    },
    // Mitä rivistä syntyy projektia perustettaessa (sama sääntö kuin OfferServiceImpl)
    projectText() {
      const label = this.$t('quotations.calc.to_project');
      if (this.row.conditional) return `${label}: ${this.$t('quotations.calc.project_nothing')}`;
      if (this.row.rowType === 'WORK') return [`${label}: ${this.$t('quotations.calc.project_work')}`, ...this.parameterTexts].join(' · ');
      // Työmaan lisänimike -ominaisuuden tyypit siirtyvät työmaalle (sama sääntö kuin OfferServiceImpl)
      if (this.row.rowType === 'ITEM' && this.row.item && isSiteAdditionalItem(this.row.item.typeId)) {
        return `${label}: ${this.$t('quotations.calc.project_item')} · ${this.row.item.name}`;
      }
      return `${label}: ${this.$t('quotations.calc.project_nothing')}`;
    },
  },
  methods: {
    // Täytetty menetelmäparametri muodossa "Otsikko: arvo"
    parameterText(def, v) {
      let text = null;
      if (v.valueItem) text = v.valueItem.name;
      else if (v.valueNumber != null && v.valueNumber !== '') text = [this.$formatNumber(v.valueNumber, '0,0.[00]'), def.unit].filter(Boolean).join(' ');
      else if (v.valueText) text = v.valueText;
      else if (v.valueBoolean) text = this.$t('yes');
      else if (v.valueDate) text = this.$formatDateNoTime(v.valueDate);
      return text ? `${def.label}: ${text}` : null;
    },
  },
};
</script>

<style scoped>
.quotation-row-view + .quotation-row-view,
div + div > .quotation-row-view {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}
.row-description {
  white-space: pre-line;
}
.quotation-row-view--conditional .row-description {
  font-style: italic;
}
.drag-handle {
  cursor: move;
}
</style>
